import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { differenceInDays } from 'date-fns';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import {
  StackedBarChartComponent,
  StackedBarChartData,
  StackedBarSubgroup,
  LegendItem
} from '../../../../../_shared/components/graphs/stacked-bar-chart/stacked-bar-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ModalService } from '../../../../../_shared/services/modals/modal.service';
import { DashboardService } from '../../../services/dashboard.service';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { DateInterval } from '../../../../../_shared/enums';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { MaintenanceCostModalComponent } from '../../modals/maintenance-cost-modal/maintenance-cost-modal.component';
import { MaintenanceInsightsResponse } from '../../../../maintenance/maintenance-dashboard/models/maintenance-dashboard-response.model';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';
import { SummaryRequest } from '../../../../../_shared/models';
import { formatValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-maintenance-cost-chart-trend',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, StackedBarChartComponent, EmptyStateComponent, TranslocoPipe],
  templateUrl: './maintenance-cost-trend.component.html',
  styleUrl: './maintenance-cost-trend.component.scss'
})
export class MaintenanceCostTrendComponent implements OnInit, OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private dashboardService = inject(DashboardService);
  private errorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);
  private modalService = inject(ModalService);
  private authService = inject(AuthService);

  chartData: StackedBarChartData[] = [];
  readonly chartHeight = 145;
  private readonly defaultYTicks = [1000000, 800000, 600000, 400000, 200000, 0];
  yTicks: number[] = this.defaultYTicks;

  // The chart component renders these strings itself, so they are rebuilt on every language change.
  subgroups: StackedBarSubgroup[] = [];
  legendItems: LegendItem[] = [];

  currencySymbol: string = '';
  currencyCode: string = '';
  title: string = 'maintenanceDashboard.charts.maintenanceCost';
  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter!: WidgetFilterData;

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    // langChanges$ replays the active language, so this also does the initial build
    this.transloco.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.buildChartLabels());

    this.filterService.filterUpdated$.subscribe(async (filter) => {
      if (Object.values(filter).every(x => x === null) || filter === null) return;

      this.widgetFilter = filter;
      await this.loadMaintenanceCostData();
    });
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadMaintenanceCostData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private buildChartLabels(): void {
    const preventative = this.transloco.translate('maintenanceDashboard.charts.preventative');
    const corrective = this.transloco.translate('maintenanceDashboard.charts.corrective');

    this.subgroups = [
      { name: 'preventative', legend: preventative, color: '#3B82F6' },
      { name: 'corrective', legend: corrective, color: '#F59E0B' }
    ];

    this.legendItems = [
      { legend: preventative, color: '#3B82F6' },
      { legend: corrective, color: '#F59E0B' }
    ];
  }

  private async loadMaintenanceCostData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds
      };

      const summaryRequest: SummaryRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
        accountIds: this.widgetFilter.accountIds
      };

      const [preventativeResponse, correctiveResponse, summaryResponse] = await Promise.all([
        this.dashboardService.getPreventiveMaintenanceCostRatio(request, this.cancelSubject),
        this.dashboardService.getCorrectiveMaintenanceCostRatio(request, this.cancelSubject),
        this.dashboardService.getTotalMaintenanceCostStats(summaryRequest, this.cancelSubject)
      ]);

      this.chartData = this.processMaintenanceCostData(
        preventativeResponse.data ?? [],
        correctiveResponse.data ?? []
      );

      this.yTicks = this.calculateYTicks(this.chartData);

      this.totalValue = summaryResponse?.data?.totalValue ?? 0;
      this.percentageChange = summaryResponse?.data?.percentageChange ?? 0;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.chartData = [];
          this.totalValue = 0;
          this.percentageChange = 0;
          this.isLoading = false;
        }
      });
    }
  }

  private processMaintenanceCostData(
    preventativeData: MaintenanceInsightsResponse[],
    correctiveData: MaintenanceInsightsResponse[]
  ): StackedBarChartData[] {
    if (preventativeData.length === 0 && correctiveData.length === 0) return [];

    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const datasets = [
      {
        label: 'preventative',
        data: preventativeData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      },
      {
        label: 'corrective',
        data: correctiveData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      }
    ];

    let groupedData: any[];

    switch (groupingType) {
      case 'hourly':
        groupedData = TimeSeriesGroupingHelper.groupByHours(datasets);
        break;
      case 'daily':
        groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
        break;
      default:
        groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }

    return groupedData.map(item => ({
      label: item.label,
      dateRange: item.dateRange,
      preventative: Number(item['preventative']) || 0,
      corrective: Number(item['corrective']) || 0
    }));
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(chartData: StackedBarChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const stackedTotals = chartData.map(item => {
      return (Number(item['preventative']) || 0) + (Number(item['corrective']) || 0);
    });

    return calculateChartYTicks(stackedTotals, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false; // Cost increase is negative
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return this.subgroups.some(subgroup => {
        const value = Number(item[subgroup.name]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    this.modalService.open(MaintenanceCostModalComponent, {
      size: 'xl',
      centered: true,
      windowClass: 'maintenance-cost-modal'
    });
  }
}